/**
 * BTI Wave 2 (Andres, 2026-09-26): the 14 safeguards due to BTI on December 3, 2026, grouped by BTI's areas.
 * First how far member firms are in each area (fully, partly and not yet in place across the firms with CDW
 * results, and how many firms have every Wave 2 control in the area in place), then a firm by area view for
 * National and the Board, then each item with how many firms have it in place today. National's own position
 * shows only where National IT has set one. BTI has not evaluated Wave 2, so there is no BTI rating to show; the
 * page says so plainly. National IT updates positions on the National view; National and the Board see them
 * read-only on Leadership; firm members never get this card. The chip, explanation and form are the Wave 1 ones.
 */
import {
  BTI_DOMAINS,
  BTI_POSITION,
  BTI_WAVE2_DUE,
  BTI_WAVE2_ITEMS,
  btiPositionCounts,
  type BtiPosition,
  type BtiPositionValue,
  type Wave2AreaReadiness,
  type Wave2FirmAreas,
} from '@grc/shared/vocabulary';
import { useState } from 'react';
import { ChartTable, InPlaceKey } from '../charts';
import { formatDate } from '../components';
import { daysUntil, itemName, PositionChip, PositionDetail, PositionForm, type Measured, type SavePosition } from './Bti';

const due = formatDate(BTI_WAVE2_DUE);
const total = BTI_WAVE2_ITEMS.length;
const SEGMENTS: Array<BtiPositionValue | 'not_set'> = ['met', 'partly', 'not_yet', 'not_set'];
const SEGMENT_LABEL: Record<BtiPositionValue | 'not_set', string> = { met: 'met', partly: 'partly met', not_yet: 'not yet met', not_set: 'not yet recorded' };

const share = (n: number, of: number) => (of ? Math.round((n / of) * 100) : 0);

/** "National's position: 3 of 14 met, 1 partly, 10 not yet set", with a bar of the same counts. */
function Wave2Summary({ positions }: { positions: BtiPosition[] }) {
  const c = btiPositionCounts(positions, 2);
  return (
    <div className="bti2-summary">
      <p className="bti2-count">
        <strong>National IT&rsquo;s position:</strong> {c.met} of {total} met, {c.partly} partly met{c.not_yet > 0 && <>, {c.not_yet} not yet met</>}, {c.not_set} not yet recorded
      </p>
      <span className="bti-bar bti2-bar" role="img" aria-label={`National IT's position on the ${total} Wave 2 requirements: ${SEGMENTS.filter((s) => c[s] > 0).map((s) => `${c[s]} ${SEGMENT_LABEL[s]}`).join(', ')}`}>
        {SEGMENTS.filter((s) => c[s] > 0).map((s) => (
          <span key={s} className={`seg bti2-${s}`} style={{ width: `${(c[s] / total) * 100}%` }} />
        ))}
      </span>
    </div>
  );
}

/** A 100% bar in three parts: fully (teal), partly (blue, hatched), not yet (the track). Every part is also said in words. */
function ThreePart({ fully, partly, notYet, label }: { fully: number; partly: number; notYet: number; label: string }) {
  const all = fully + partly + notYet;
  const w = (v: number) => `${all ? (v / all) * 100 : 0}%`;
  return (
    <span className="lead-gap-bar bti2-area-bar" role="img" aria-label={label}>
      {fully > 0 && <span className="seg in-place-now" style={{ width: w(fully) }} />}
      {partly > 0 && <span className="seg partly" style={{ width: w(partly) }} />}
      {notYet > 0 && <span className="seg in-place" style={{ width: w(notYet) }} />}
    </span>
  );
}

/**
 * How far member firms are in each BTI Wave 2 area: of every firm's Wave 2 controls in the area, the share fully,
 * partly and not yet in place, and how many firms have every one of them in place (at least partly) and fully.
 */
function AreaReadiness({ areas }: { areas: Wave2AreaReadiness[] }) {
  return (
    <div className="bti2-readiness">
      <ul className="plain bti2-areas" aria-label="Wave 2 readiness by BTI area">
        {areas.map((a) => {
          const rated = a.fully + a.partly + a.notYet;
          return (
            <li key={a.domain} className="bti2-area">
              <span className="bti2-area-name">
                <strong>{a.domain}</strong>
                <span className="muted small"> {a.controls === 1 ? '1 requirement' : `${a.controls} requirements`}</span>
              </span>
              {rated > 0 ? (
                <>
                  <ThreePart
                    fully={a.fully}
                    partly={a.partly}
                    notYet={a.notYet}
                    label={`${a.domain}: of the Wave 2 requirements across ${a.firmsRated} firms with CDW results, ${share(a.fully, rated)}% fully in place, ${share(a.partly, rated)}% partly, ${share(a.notYet, rated)}% not yet`}
                  />
                  <span className="small bti2-area-share">
                    <strong>{share(a.fully, rated)}% fully</strong>, {share(a.partly, rated)}% partly
                  </span>
                  <span className="small bti2-area-firms">
                    <strong>
                      {a.firmsAllInPlace} of {a.firmsRated}
                    </strong>{' '}
                    firms have all {a.controls} in place, {a.firmsAllFully} of them fully
                  </span>
                </>
              ) : (
                <span className="muted small bti2-area-share">No CDW results yet</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Firm by area, for National and the Board: each cell the firm's Wave 2 controls in that area, fully, partly and not yet. */
function FirmByArea({ byFirm, areas }: { byFirm: Wave2FirmAreas[]; areas: Wave2AreaReadiness[] }) {
  const cellText = (c: Wave2FirmAreas['areas'][number]) =>
    c.rated === 0 ? 'Not rated' : `${c.fully + c.partly} of ${c.rated} in place (${c.fully} fully)`;
  return (
    <>
      <div className="table-wrap" tabIndex={0} role="region" aria-label="Wave 2 readiness by firm and area">
        <table className="bti2-matrix">
          <caption className="sr-only">Each firm with CDW results, and its Wave 2 requirements in place in each BTI area</caption>
          <thead>
            <tr>
              <th scope="col">Firm</th>
              {areas.map((a) => (
                <th scope="col" key={a.domain}>
                  {a.domain}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {byFirm.map((f) => (
              <tr key={f.firmCode}>
                <th scope="row">{f.firmName}</th>
                {f.areas.map((c) => (
                  <td key={c.domain}>
                    {c.rated > 0 ? (
                      <span className="bti2-cell">
                        <ThreePart fully={c.fully} partly={c.partly} notYet={c.notYet} label={`${f.firmName}, ${c.domain}: ${c.fully} of ${c.rated} fully in place, ${c.partly} partly, ${c.notYet} not yet`} />
                        <span className="small">{cellText(c)}</span>
                      </span>
                    ) : (
                      <span className="muted small">Not rated</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ChartTable
        caption="Wave 2 readiness by firm and area"
        head={['Firm', ...areas.map((a) => a.domain)]}
        rows={byFirm.map((f) => [f.firmName, ...f.areas.map((c) => (c.rated ? `${c.fully + c.partly} of ${c.rated} in place (${c.fully} fully)` : 'Not rated'))])}
      />
    </>
  );
}

export function BtiWave2Card({ measured, areas, positions, onSavePosition, headingLevel = 3, showRefs = true }: {
  /** Today's measure for each Wave 2 control, across firms with CDW results. */
  measured: Measured[];
  /** Readiness by BTI area, overall and firm by firm (National and Board only). */
  areas?: { areas: Wave2AreaReadiness[]; byFirm: Wave2FirmAreas[] };
  /** National's Wave 2 positions (National and Board only). */
  positions?: BtiPosition[];
  /** National IT on the National view only: shows an Update button per item. */
  onSavePosition?: SavePosition;
  headingLevel?: 2 | 3;
  /** CIS references beside each item: on for the National view, off on Leadership (DEC-16). */
  showRefs?: boolean;
}) {
  const [shown, setShown] = useState<number | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const H = headingLevel === 2 ? 'h2' : 'h3';
  const D = headingLevel === 2 ? 'h3' : 'h4';
  const days = daysUntil(BTI_WAVE2_DUE);
  const wave2 = positions?.filter((p) => p.wave === 2);
  return (
    <section className="card lead-wide bti2-card" aria-labelledby="bti2-h">
      <H id="bti2-h">Wave 2: getting ready for {due}</H>
      <p className="muted small">
        {total} requirements{days > 0 && <>, {days} days left</>}; BTI rates them after submission.
      </p>
      {wave2 && wave2.length > 0 && <Wave2Summary positions={wave2} />}
      {areas && areas.areas.length > 0 && (
        <>
          <D className="bti2-sub">Progress by area</D>
          <p className="muted small">Share of each area&rsquo;s requirements in place, across firms with CDW results</p>
          <InPlaceKey />
          <AreaReadiness areas={areas.areas} />
          {areas.byFirm.length > 0 && (
            <>
              <D className="bti2-sub">Firm by area</D>
              <FirmByArea byFirm={areas.byFirm} areas={areas.areas} />
            </>
          )}
        </>
      )}
      <D className="bti2-sub">Each Wave 2 requirement</D>
      <p className="muted small">Each requirement counts the firms that rated it, so its total can be lower than an area&rsquo;s.</p>
      {BTI_DOMAINS.map((d) => {
        const inDomain = BTI_WAVE2_ITEMS.filter((i) => i.domain === d);
        if (inDomain.length === 0) return null;
        return (
          <div key={d} className="bti2-domain">
            <D className="bti2-domain-name">{d}</D>
            <ol className="plain bti-items">
              {inDomain.map((i) => {
                const x = measured.find((m) => m.controlId === i.controlId);
                // In place counts fully or partly (2026-09-26); the bar still shows the partly share on its own.
                const fully = x ? x.inPlace - x.partly : 0;
                const notYet = x ? x.rated - x.inPlace : 0;
                const p = wave2?.find((q) => q.itemNo === i.no);
                return (
                  <li key={i.no} className="bti-item bti2-item">
                    <span className="bti-item-text">
                      <span className="muted small">{i.no}. </span>
                      {i.title}
                      {showRefs && <span className="muted small"> CIS {i.controlId}</span>}
                    </span>
                    {x && x.rated > 0 ? (
                      <ThreePart fully={fully} partly={x.partly} notYet={notYet} label={`${i.title}: in place at ${x.inPlace} (fully at ${fully}, partly at ${x.partly}), not yet at ${notYet}, of ${x.rated} firms with CDW results`} />
                    ) : (
                      <span className="muted small">No CDW results yet</span>
                    )}
                    <span className="small bti-now">{x && x.rated > 0 ? `In place at ${x.inPlace} of ${x.rated} firms (${fully} fully)` : ''}</span>
                    {/* National's chip only where it has set a position; no empty column for the rest. */}
                    <span className="bti-actions">
                      {p && <PositionChip p={p} expanded={shown === i.no} onToggle={() => setShown(shown === i.no ? null : i.no)} />}
                      {onSavePosition && editing !== i.no && (
                        <button type="button" className="pill small-pill bti-update" onClick={() => setEditing(i.no)}>
                          Update<span className="sr-only"> National IT&rsquo;s position on {itemName(2, i.no)}</span>
                        </button>
                      )}
                    </span>
                    {p && shown === i.no && editing !== i.no && <PositionDetail p={p} footnote={<>BTI evaluates Wave 2 after the {due} submission.</>} />}
                    {onSavePosition && editing === i.no && (
                      <div className="bti-position">
                        <PositionForm
                          wave={2}
                          itemNo={i.no}
                          title={i.title}
                          intro={<>National IT&rsquo;s own position on this requirement today, ahead of the {due} submission. BTI has not rated Wave 2 yet.</>}
                          current={p}
                          measured={x}
                          onSave={onSavePosition}
                          onDone={(saved) => {
                            setEditing(null);
                            if (saved) setShown(i.no);
                          }}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        );
      })}
      <p className="legend small">
        <span className="gap-key in-place-now" aria-hidden="true" /> Fully in place today <span className="gap-key partly" aria-hidden="true" /> Partly in place <span className="gap-key in-place" aria-hidden="true" /> Not yet in place
        {wave2?.length ? (
          <>
            {' '}<span className="chip bti-national legend-chip" aria-hidden="true">Met</span> National IT&rsquo;s position ({Object.values(BTI_POSITION).map((v) => v.label).join(', ')})
          </>
        ) : null}
      </p>
    </section>
  );
}
